import { useGetQuery, useLazyGetQuery, usePostMutation } from "slices/api";
import {
  APPLICATION_COMMENT,
  APPLICATION_DETAILS,
  BILLING_CALCULATIONS,
  FIRE_SAFETY_APPLICATION,
  FIRE_SAFETY_COMMENT,
} from "tagTypes";

export const useApplicationCommentQuery = (
  applicationId,
  query = {},
  isFireSafety
) =>
  useGetQuery({
    endpoint: isFireSafety
      ? `/comment/building-information/${applicationId}`
      : `/application/${applicationId}/application-comment`,
    query,
    tags: [APPLICATION_COMMENT, FIRE_SAFETY_APPLICATION],
  });

export const useAdminCommentsByIdQuery = (
  query = {},
  isFireSafety = false,
  buildingInformationId
) =>
  useGetQuery({
    endpoint: isFireSafety
      ? `/admin-comment/${buildingInformationId}`
      : "/admin-comment",
    query,
    tags: [
      APPLICATION_COMMENT,
      APPLICATION_DETAILS,
      BILLING_CALCULATIONS,
      FIRE_SAFETY_COMMENT,
    ],
  });

export const useLazyGetApplicationCommentQuery = () => {
  const [adminComments, data] = useLazyGetQuery();
  const adminComment = async (
    applicationId,
    query = {},
    isFireSafety = false
  ) =>
    await adminComments(
      {
        endpoint: isFireSafety
          ? `/comment/building-information/${applicationId}`
          : `/application/${applicationId}/application-comment`,
        query,
        tags: [APPLICATION_COMMENT, FIRE_SAFETY_COMMENT],
      },
      true
    ).unwrap();
  return [adminComment, data];
};

export const useLazyGetBuildingObservationCommentQuery = () => {
  const [observationsComments, data] = useLazyGetQuery();
  const observationComment = async (applicationId, query = {}) =>
    await observationsComments(
      {
        endpoint: `/comment/building-information/${applicationId}/inspection-observation`,
        query,
        tags: [FIRE_SAFETY_COMMENT],
      },
      true
    ).unwrap();
  return [observationComment, data];
};

export const useLazyGetBuildingObservationAdminCommentsByIdQuery = () => {
  const [observationsComments, data] = useLazyGetQuery();
  const observationComment = async (applicationId, query = {}) =>
    await observationsComments(
      {
        endpoint: `/admin-comment/building-information/${applicationId}/inspection-observation`,
        query,
        tags: [FIRE_SAFETY_COMMENT],
      },
      true
    ).unwrap();
  return [observationComment, data];
};

export const useLazyAdminCommentQuery = () => {
  const [adminComments, data] = useLazyGetQuery();
  const adminComment = async (query = {}) =>
    await adminComments(
      {
        endpoint: "/admin-comment",
        query,
        tags: [APPLICATION_COMMENT],
      },
      true
    ).unwrap();
  return [adminComment, data];
};

export const usePostApplicationCommentMutation = () => {
  const [applicationComments, data] = usePostMutation();
  const applicationComment = async (
    payload,
    query = {},
    isFireSafety = false
  ) =>
    await applicationComments({
      endpoint: isFireSafety
        ? "/comment/building-information"
        : "/application-comment",
      query,
      payload: payload || {},
      toastMessage: "Application request comment added successfully",
      tags: [APPLICATION_COMMENT, FIRE_SAFETY_COMMENT],
    }).unwrap();
  return [applicationComment, data];
};

export const usePostBuildingObservationCommentMutation = () => {
  const [observationComments, data] = usePostMutation();
  const observationComment = async (payload, query = {}) =>
    await observationComments({
      endpoint: "/comment/inspection-observation",
      query,
      payload: payload || {},
      toastMessage: "Observation comment added successfully",
      tags: [FIRE_SAFETY_COMMENT],
    }).unwrap();
  return [observationComment, data];
};

export const usePostAdminBuildingObservationCommentMutation = () => {
  const [observationComments, data] = usePostMutation();
  const observationComment = async (payload, query = {}) =>
    await observationComments({
      endpoint: "/admin-comment",
      query,
      payload: payload || {},
      toastMessage: "Observation comment added successfully",
      tags: [FIRE_SAFETY_COMMENT],
    }).unwrap();
  return [observationComment, data];
};
